import React, { createContext, useContext, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
  TableLayoutContextValue,
  TableDimensions,
  TableImageRect,
  SeatAnchorPosition,
  SeatAnchorConfig,
} from './tableLayoutTypes';
import { TABLE_RAIL_ASPECT_RATIO, TABLE_RAIL_WIDTH, TABLE_RAIL_HEIGHT } from '../shell/feltMaskPath';

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

let cachedProbe: HTMLDivElement | null = null;

export function getSafeAreaInsets(): SafeAreaInsets {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  try {
    if (!cachedProbe || !document.body.contains(cachedProbe)) {
      const probe = document.createElement('div');
      probe.id = 'safe-area-probe';
      probe.style.cssText = [
        'position: fixed',
        'top: 0',
        'left: 0',
        'width: 0',
        'height: 0',
        'visibility: hidden',
        'pointer-events: none',
        'padding-top: env(safe-area-inset-top, 0px)',
        'padding-bottom: env(safe-area-inset-bottom, 0px)',
        'padding-left: env(safe-area-inset-left, 0px)',
        'padding-right: env(safe-area-inset-right, 0px)',
      ].join(';');
      document.body.appendChild(probe);
      cachedProbe = probe;
    }

    const computed = window.getComputedStyle(cachedProbe);
    return {
      top: parseFloat(computed.paddingTop) || 0,
      bottom: parseFloat(computed.paddingBottom) || 0,
      left: parseFloat(computed.paddingLeft) || 0,
      right: parseFloat(computed.paddingRight) || 0,
    };
  } catch {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }
}

export interface CalculateTableDimensionsParams {
  viewportWidth: number;
  viewportHeight: number;
  safeAreaInsets?: Partial<SafeAreaInsets>;
  targetAspect?: number;
  baseWidth?: number;
  baseHeight?: number;
}

export function calculateTableDimensions({
  viewportWidth,
  viewportHeight,
  safeAreaInsets = {},
  targetAspect = TABLE_RAIL_ASPECT_RATIO,
  baseWidth = TABLE_RAIL_WIDTH,
  baseHeight = TABLE_RAIL_HEIGHT,
}: CalculateTableDimensionsParams): TableDimensions {
  const vWidth = Math.max(0, viewportWidth);
  const vHeight = Math.max(0, viewportHeight);
  const isPortrait = vHeight >= vWidth;

  const insets: SafeAreaInsets = {
    top: safeAreaInsets.top ?? 0,
    bottom: safeAreaInsets.bottom ?? 0,
    left: safeAreaInsets.left ?? 0,
    right: safeAreaInsets.right ?? 0,
  };

  // Horizontal clearance:
  // Account for safe areas (e.g. landscape notches) + side padding so table rails & seats don't touch screen edge
  const sideMargin = isPortrait ? 14 : 20;
  const reservedWidth = insets.left + insets.right + (sideMargin * 2);
  const availableWidth = Math.max(160, vWidth - reservedWidth);

  // Vertical clearance:
  // Top: status bar / notch / camera island + clearance for North player avatar, statement header graphic with gap, and notification HUD
  const topMargin = isPortrait ? 38 : 24;
  const reservedTop = Math.max(insets.top, 8) + topMargin;

  // Bottom: safe area home bar + clearance for South player hand fan and floating action controls.
  // Calibrated clearance with South hand moved down and cards reduced 10%, shifting table down for header room.
  const bottomMargin = isPortrait ? (vHeight < 720 ? 104 : 120) : 112;
  const reservedBottom = Math.max(insets.bottom, 8) + bottomMargin;

  const availableHeight = Math.max(260, vHeight - (reservedTop + reservedBottom));

  // Sizing based on the smaller of available width / height constraints
  // using a single uniform scale factor so aspect ratio is STRICTLY preserved.
  const scaleByWidth = availableWidth / baseWidth;
  const scaleByHeight = availableHeight / baseHeight;
  const uniformScale = Math.min(scaleByWidth, scaleByHeight);

  // Calculate width and height using the uniform scale factor
  let width = Math.round(baseWidth * uniformScale);
  let height = Math.round(baseHeight * uniformScale);

  // Exact constraint boundary enforcement while preserving targetAspect
  if (width > availableWidth) {
    width = availableWidth;
    height = Math.round(width / targetAspect);
  }
  if (height > availableHeight) {
    height = availableHeight;
    width = Math.round(height * targetAspect);
  }

  // Normalized scale factor relative to standard 370px width baseline
  const scale = width / 370;
  const isCompact = width < 330 || height < 520;

  // Elevation offset to shift table higher towards top clearance,
  // compensating for the larger bottom clearance needed by South's hand fan even with larger sized cards.
  // Calibrated with downward shift to move table and rail-anchored seats lower for extra header room (+5px elevated).
  const verticalOffset = Math.max(0, Math.round((reservedBottom - reservedTop) / 2) - 21);

  return {
    width,
    height,
    scale,
    isCompact,
    aspectRatio: targetAspect,
    verticalOffset,
  };
}

const defaultDimensions: TableDimensions = {
  width: 370,
  height: Math.round(370 / TABLE_RAIL_ASPECT_RATIO),
  scale: 1,
  isCompact: false,
  aspectRatio: TABLE_RAIL_ASPECT_RATIO,
  verticalOffset: 0,
};

/**
 * Authoritative normalized center positions of the avatar wells in the table rail image.
 * Derived from the actual rendered cushion wells in tablerail_wood and tablerail_gold.
 */
export const SEAT_IMAGE_OFFSETS: Record<SeatAnchorPosition, { x: number; y: number }> = {
  north: { x: 0.500, y: 0.0570 },
  south: { x: 0.500, y: 0.9000 },
  west:  { x: 0.105, y: 0.4750 },
  east:  { x: 0.880, y: 0.4750 },
};

const defaultAnchors: Record<SeatAnchorPosition, SeatAnchorConfig> = {
  north: {
    position: 'north',
    style: {
      top: '5.7%',
      left: '50.0%',
      transform: 'translate(-50%, -50%)',
    },
    podPlacement: 'top',
    badgePlacement: 'north',
    pillPlacement: 'top',
    rotation: 0,
  },
  south: {
    position: 'south',
    style: {
      top: '90.0%',
      left: '50.0%',
      transform: 'translate(-50%, -50%)',
    },
    podPlacement: 'bottom',
    badgePlacement: 'south',
    pillPlacement: 'bottom',
    rotation: 0,
  },
  west: {
    position: 'west',
    style: {
      left: '10.5%',
      top: '47.5%',
      transform: 'translate(-50%, -50%)',
    },
    podPlacement: 'left',
    badgePlacement: 'west',
    pillPlacement: 'top',
    rotation: 0,
  },
  east: {
    position: 'east',
    style: {
      left: '88.0%',
      top: '47.5%',
      transform: 'translate(-50%, -50%)',
    },
    podPlacement: 'right',
    badgePlacement: 'east',
    pillPlacement: 'top',
    rotation: 0,
  },
};

const TableLayoutContext = createContext<TableLayoutContextValue>({
  containerRef: { current: null },
  tableRef: { current: null },
  tableImageRef: { current: null },
  dimensions: defaultDimensions,
  seatAnchors: defaultAnchors,
  isPortrait: true,
  imageRect: null,
  onTableImageLoad: () => {},
  updateTableImageBounds: () => {},
});

export const useTableLayout = (): TableLayoutContextValue => {
  return useContext(TableLayoutContext);
};

export interface TableLayoutProviderProps {
  children: React.ReactNode;
}

export const TableLayoutProvider: React.FC<TableLayoutProviderProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const tableImageRef = useRef<HTMLImageElement | null>(null);

  const [imageRect, setImageRect] = useState<TableImageRect | null>(null);

  const [dimensions, setDimensions] = useState<TableDimensions>(() => {
    if (typeof window !== 'undefined') {
      return calculateTableDimensions({
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        safeAreaInsets: getSafeAreaInsets(),
      });
    }
    return defaultDimensions;
  });

  const [isPortrait, setIsPortrait] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight >= window.innerWidth;
    }
    return true;
  });

  const rafIdRef = useRef<number | null>(null);

  const updateTableImageBounds = useCallback(() => {
    const img = tableImageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) return;

    let renderedX = rect.left;
    let renderedY = rect.top;
    let renderedW = rect.width;
    let renderedH = rect.height;

    const naturalW = img.naturalWidth || TABLE_RAIL_WIDTH;
    const naturalH = img.naturalHeight || TABLE_RAIL_HEIGHT;
    const naturalAspect = naturalW / naturalH;
    const elemAspect = rect.width / (rect.height || 1);

    if (typeof window !== 'undefined') {
      const computedStyle = window.getComputedStyle(img);
      if (computedStyle?.objectFit === 'contain') {
        if (elemAspect > naturalAspect) {
          renderedW = rect.height * naturalAspect;
          renderedX = rect.left + (rect.width - renderedW) / 2;
        } else {
          renderedH = rect.width / naturalAspect;
          renderedY = rect.top + (rect.height - renderedH) / 2;
        }
      }
    }

    const newRect: TableImageRect = {
      x: Math.round(renderedX),
      y: Math.round(renderedY),
      width: Math.round(renderedW),
      height: Math.round(renderedH),
    };

    setImageRect(prev => {
      if (
        prev &&
        prev.x === newRect.x &&
        prev.y === newRect.y &&
        prev.width === newRect.width &&
        prev.height === newRect.height
      ) {
        return prev;
      }
      return newRect;
    });
  }, []);

  const onTableImageLoad = useCallback(() => {
    updateTableImageBounds();
  }, [updateTableImageBounds]);

  const updateDimensions = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const container = containerRef.current;
      const vWidth = typeof window !== 'undefined' ? window.innerWidth : 370;
      const vHeight = typeof window !== 'undefined' ? window.innerHeight : 640;

      // Use container bounds if container is constrained (e.g. embedded or split-view)
      const effectiveWidth = (container?.clientWidth && container.clientWidth < vWidth - 32)
        ? container.clientWidth
        : vWidth;
      const effectiveHeight = (container?.clientHeight && container.clientHeight < vHeight - 32)
        ? container.clientHeight
        : vHeight;

      const portrait = effectiveHeight >= effectiveWidth;
      setIsPortrait(prev => (prev === portrait ? prev : portrait));

      const safeInsets = getSafeAreaInsets();
      const newDimensions = calculateTableDimensions({
        viewportWidth: effectiveWidth,
        viewportHeight: effectiveHeight,
        safeAreaInsets: safeInsets,
      });

      setDimensions(prev => {
        if (
          prev.width === newDimensions.width &&
          prev.height === newDimensions.height &&
          prev.scale === newDimensions.scale &&
          prev.isCompact === newDimensions.isCompact &&
          prev.aspectRatio === newDimensions.aspectRatio &&
          prev.verticalOffset === newDimensions.verticalOffset
        ) {
          return prev;
        }
        return newDimensions;
      });

      updateTableImageBounds();
    });
  }, [updateTableImageBounds]);

  useEffect(() => {
    updateDimensions();

    // If image is already complete (cached or loaded)
    if (tableImageRef.current?.complete) {
      updateTableImageBounds();
    }

    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDimensions);
    }

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(() => updateDimensions());
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
      if (typeof window !== 'undefined' && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateDimensions);
      }
      resizeObserver?.disconnect();
    };
  }, [updateDimensions, updateTableImageBounds]);

  const seatAnchors = useMemo<Record<SeatAnchorPosition, SeatAnchorConfig>>(() => {
    return defaultAnchors;
  }, []);

  const value = useMemo<TableLayoutContextValue>(() => ({
    containerRef,
    tableRef,
    tableImageRef,
    dimensions,
    seatAnchors,
    isPortrait,
    imageRect,
    onTableImageLoad,
    updateTableImageBounds,
  }), [dimensions, seatAnchors, isPortrait, imageRect, onTableImageLoad, updateTableImageBounds]);

  return (
    <TableLayoutContext.Provider value={value}>
      {children}
    </TableLayoutContext.Provider>
  );
};

